// ==================================================================================================
// The MIT License (MIT)
// ==================================================================================================
// Copyright (c) 2025 TerriFlux
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.
// ==================================================================================================
// Author        : Vincent LE DOZE & Vincent CLAVEL & Julien Alapetite for TerriFlux
// ==================================================================================================

import React, { ChangeEvent, useEffect, useState } from 'react'
import { Checkbox, Box, Button, Input, Select, Text, Divider, Alert, AlertIcon } from '@chakra-ui/react'
import { Tabs, TabList, TabPanels, Tab, TabPanel } from '@chakra-ui/react'
import { MenuDraggable } from '../topmenus/SankeyMenus'
import { Class_ApplicationData } from '../../types/ApplicationData'
import FileSaver from 'file-saver'
import { decompressGzipDataFixed, decompressUploadedFileUniversal } from '../../Persistence/UniversalJSONCompression'
import { Type_JSON } from '../../types/Utils'
import { ActionButtons, ProcessTerminal } from './PersistenceProcessDialogTerminal'
import {
  CONVERTER_CONFIGS, ConverterConfig, FormatConfigStructure, FormatType, getDefaultInputOptions, getDefaultOutputOptions,
  getInitialFormat, hasOptionsFormat
} from './PersistenceProcessDialogConfigs'
import { OSTooltip, WrapperBoxSubSectionMenu } from '../configmenus/MenuCommon'
import { AutoLayoutSpacingInputs } from './MenuContextWidgetFactory'
import { AutoGeneratedOptions } from './PersistenceProcessDialogOptions'
import { applyRandomColors } from '../../Algorithms/Colors'
import { Class_DataTagGroup } from '../../types/TagGroup'
import { LinkStyle, NodeProductStyle, NodeSectorStyle, NodeStyle } from '../../Elements/ElementStyle'

/**
 * Types de formats supportés
 */
type FileFormat = 'excel' | 'json' | 'blob'

/**
 * Configuration pour chaque format
 */
const FORMAT_CONFIG: Record<FileFormat, {
  extension: string
  accept: string
  label: string
  icon: string
}> = {
  excel: {
    extension: '.xlsx',
    accept: '.xlsx,.ods',
    label: 'Fichier Excel',
    icon: '📊'
  },
  json: {
    extension: '.json',
    accept: '.json,.gz',
    label: 'Fichier JSON',
    icon: '📄'
  },
  blob: {
    extension: '.json',
    accept: '.json,.gz',
    label: 'Sankey courant',
    icon: '📄'
  }
}

// Keyed by dialog_name so we can re-read the file fresh from disk on each
// "Ouvrir" click and survive modal close/reopen without losing the selection.
const stored_file_handles = new Map<string, FileSystemFileHandle>()

const supports_fs_access = typeof window !== 'undefined' && 'showOpenFilePicker' in window

const pickerTypesForFormat = (format: FormatType): Array<{ description: string; accept: Record<string, string[]> }> | undefined => {
  if (format === 'excel') {
    return [{
      description: 'Excel',
      accept: {
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
        'application/vnd.oasis.opendocument.spreadsheet': ['.ods']
      }
    }]
  }
  if (format === 'json') {
    return [{
      description: 'JSON',
      accept: { 'application/json': ['.json'], 'application/gzip': ['.gz'] }
    }]
  }
  return undefined
}

interface FileFormatSectionProps {
  // Config
  section_type: 'input' | 'output'
  app_data: Class_ApplicationData
  lang: 'en' | 'fr'

  // Format config
  format_config: {
    fixed?: FormatType
    options?: FormatType[]
  }
  required: boolean

  // États
  current_format: FormatType
  set_current_format: (format: FormatType) => void

  set_file?: (file: Blob | undefined) => void

  // File System Access API: when provided, replaces the plain <input type="file">
  // with a button that opens showOpenFilePicker and keeps a reusable handle.
  pick_file_handle?: () => void | Promise<void>
  current_file_name?: string

  // Options
  options_base: Record<string, unknown>
  set_options_base: (opts: Record<string, unknown>) => void
  options_excel: Record<string, unknown>
  set_options_excel: (opts: Record<string, unknown>) => void
  options_json: Record<string, unknown>
  set_options_json: (opts: Record<string, unknown>) => void
  input_config: FormatConfigStructure
  output_config: FormatConfigStructure
}

export const FileFormatSection = ({
  section_type,
  app_data,
  lang,
  format_config,
  required,
  current_format,
  set_current_format,
  set_file,
  pick_file_handle,
  current_file_name,
  options_base,
  set_options_base,
  options_excel,
  set_options_excel,
  options_json,
  set_options_json,
  input_config,
  output_config
}: FileFormatSectionProps) => {
  const { t } = app_data
  const is_input = section_type === 'input'
  const config_to_use = is_input ? input_config : output_config
  const display = Object.keys(config_to_use['base']).length > 0 || Object.keys(config_to_use[current_format]).length > 0
  const gridTemplateColumns = hasOptionsFormat(format_config) && format_config.options.length > 1 ? '1fr 2fr 3fr' : '1fr'

  if (!required) return <></>

  // Nom du deuxième tab selon le format
  const getFormatTabLabel = () => {
    if (current_format === 'json') return 'Diagramme'
    if (current_format === 'excel') return 'Excel'
    return 'Format'
  }

  return (
    <Box>
      <Box
        as='span'
        layerStyle='menu_sub_section_title'
        textStyle='title_sub_section'
      >
        {is_input
          ? t('ProcessDialog.input_parameters')
          : t('ProcessDialog.output_parameters')
        }
      </Box>

      <Box layerStyle='menuconfigpanel_grid'>
        {/* Sélecteur de format - seulement si multiple choix */}
        <Box display={'grid'} gridTemplateColumns={gridTemplateColumns}>
          {hasOptionsFormat(format_config) && format_config.options.length > 1 && (<>
            <Text fontWeight='bold' mb={2}>
              {is_input
                ? t('ProcessDialog.input_format')
                : t('ProcessDialog.output_format')
              }
            </Text>
            <Select
              value={current_format}
              onChange={(e: { target: { value: string } }) => set_current_format(e.target.value as FormatType)}
            >
              {format_config.options.map((fmt: FormatType) => (
                <option key={fmt} value={fmt}>
                  {//@ts-expect-error xxx
                    FORMAT_CONFIG[fmt].icon} {FORMAT_CONFIG[fmt].label}
                </option>
              ))}
            </Select></>
          )}

          {/* Input file - seulement pour section input */}
          {current_format != 'blob' && is_input && (hasOptionsFormat(format_config)) && set_file && (
            pick_file_handle ? (
              <Box display='grid' gridTemplateColumns='1fr 2fr' gap={2} alignItems='center'>
                <Button size='sm' onClick={() => { void pick_file_handle() }}>
                  {current_file_name ? 'Changer...' : 'Parcourir...'}
                </Button>
                <Text fontSize='sm' noOfLines={1} title={current_file_name}>
                  {current_file_name ?? ''}
                </Text>
              </Box>
            ) : (
              <Input
                type="file"
                accept={//@ts-expect-error xxx
                  FORMAT_CONFIG[current_format].accept}
                onChange={(evt: ChangeEvent<HTMLInputElement>) => {
                  const files = (evt.target as HTMLInputElement).files
                  set_file(files?.[0])
                }}
              />
            )
          )}
        </Box>


        {/* Options de format sur deux colonnes */}
        {(hasOptionsFormat(format_config) && display) && (
          <WrapperBoxSubSectionMenu
            new_data={app_data}
            title={is_input
              ? t('ProcessDialog.input_options')
              : t('ProcessDialog.output_options')
            }
            with_border={false}
            is_open={false}
          >
            <Box display='grid' gridTemplateColumns='1fr 1fr' gap={4}>
              {/* Colonne Base */}
              <Box>
                <Text fontWeight='bold' mb={2}>Base</Text>
                <AutoGeneratedOptions
                  app_data={app_data}
                  format='base'
                  config={config_to_use}
                  options={options_base}
                  setOptions={set_options_base}
                  lang={lang}
                />
              </Box>

              {/* Colonne Options spécifiques au format */}
              <Box>
                <Text fontWeight='bold' mb={2}>{getFormatTabLabel()}</Text>
                {current_format === 'excel' && (
                  <AutoGeneratedOptions
                    app_data={app_data}
                    format='excel'
                    config={config_to_use}
                    options={options_excel}
                    setOptions={set_options_excel}
                    lang={lang}
                  />
                )}
                {current_format === 'json' && (
                  <AutoGeneratedOptions
                    app_data={app_data}
                    format='json'
                    config={config_to_use}
                    options={options_json}
                    setOptions={set_options_json}
                    lang={lang}
                  />
                )}
              </Box>
            </Box>
          </WrapperBoxSubSectionMenu>
        )}
      </Box>
    </Box>
  )
}

/**
 * Reset Drawing area with extracted json data from an excel file
 *
 * @param {*} applicationData
 * @param {string} text
 */
export const retrieveJSONResults = (
  app_data: Class_ApplicationData,
  JSON_data: Type_JSON,
  apply_layout_current_sankey: ConstrainBoolean,
  _kwargs: Type_JSON,
  h_spacing?: number,
  v_spacing?: number,
  optimize_crossing: boolean = true,
  sources_mode: 'before_neighbor' | 'left_extremity' = 'before_neighbor',
  sinks_mode: 'after_neighbor' | 'right_extremity' = 'after_neighbor'
) => {
  // // Failsafe
  // if (text === '{}')
  //   return
  const current_json = app_data.toJSON()
  //const data_as_json = JSON.parse(text) as Type_JSON
  JSON_data['version'] = app_data.version // Avoid converter process
  app_data.fromJSON(JSON_data as Type_JSON, {} /*output_options_json*/, false)

  app_data.drawing_area.bypass_redraws = true
  app_data.drawing_area.sankey.nodes_list.forEach(n => {
    const tagg = app_data.drawing_area.sankey.node_taggs_dict['type de noeud']
    if (!tagg) {
      return
    }
    const product_tag = tagg.tags_dict['produit']
    const sector_tag = tagg.tags_dict['secteur']
    //const echange_tag = tagg.tags_dict['echange']
    if (n.hasGivenTag(product_tag)) {
      // Vérifier si les styles corrects ne sont pas déjà appliqués
      if (!n.hasStyle(NodeProductStyle)) {
        // Retirer tous les styles personnalisés existants
        n.removeAllStyles()

        // Ajouter les styles appropriés
        n.addStyle(app_data.drawing_area.sankey.styles_dict[NodeStyle])
        n.addStyle(app_data.drawing_area.sankey.styles_dict[NodeProductStyle])
      }
    }
    // Pour les noeuds secteur
    else if (n.hasGivenTag(sector_tag)) {
      // Vérifier si les styles corrects ne sont pas déjà appliqués
      if (!n.hasStyle(NodeSectorStyle)) {
        // Retirer tous les styles personnalisés existants
        n.removeAllStyles()

        // Ajouter les styles appropriés
        n.addStyle(app_data.drawing_area.sankey.styles_dict[NodeStyle])
        n.addStyle(app_data.drawing_area.sankey.styles_dict[NodeSectorStyle])
      }
    }
  })
  app_data.drawing_area.legend.masked = false
  if (app_data.drawing_area.sankey.flux_taggs_list.length > 0) {
    app_data.drawing_area.sankey.flux_taggs_list[0].use_colors = true
  } else if (app_data.drawing_area.sankey.node_taggs_list.filter(tagg => tagg.id != 'type de noeud').length == 0) {
    app_data.drawing_area.sankey.styles_dict[LinkStyle].shape_color_rule = 'flow'
    applyRandomColors(app_data, app_data.drawing_area.sankey.links_list)
  }
  const unit_taggs = app_data.drawing_area.sankey.getTagGroupsAsList('data_taggs').filter(tagg => tagg.is_unit) as Class_DataTagGroup[]
  if (unit_taggs.length > 0) {
    app_data.drawing_area.sankey.styles_dict['default'].value_label_unit_type = 'unit_tag'
    app_data.drawing_area.sankey.styles_dict['default'].value_label_unit_visible = true
  }

  // Case 1 : Apply extracted layout if present -> contains positions
  if (apply_layout_current_sankey) {
    app_data.drawing_area.nodePositioning.computeScale()
    app_data.updateFromJSON(current_json)
  } else if (JSON_data['layout']) {
    app_data.drawing_area.nodePositioning.computeScale()
    app_data.updateFromJSON(JSON_data['layout'] as Type_JSON)
  } else {
    app_data.drawing_area.nodePositioning.computeAutoSankeyWithToast(true, optimize_crossing, h_spacing, v_spacing, sources_mode, sinks_mode)
    app_data.drawing_area.sankey.setTrade(true)
  }
  app_data.drawing_area.draw()
  app_data.menu_configuration.updateComponentRelatedToStyles()

}

/**
 * Composant de conversion universelle de formats
 */
export const UniversalFileConverter = ({
  app_data,
  dialog_name,
  input_config,
  output_config
}: {
  app_data: Class_ApplicationData,
  dialog_name: string;
  input_config: FormatConfigStructure,
  output_config: FormatConfigStructure
}) => {
  //const config = CONVERTER_CONFIGS[config_key] as ConverterConfig
  const { t, url_prefix } = app_data
  const lang = (app_data.i18n?.language === 'fr' ? 'fr' : 'en') as 'en' | 'fr'
  //const [layout_open, setLayoutOpen] = useState(false)
  const [auto_layout, setAutoLayout] = useState(false)
  const [auto_load, setAutoLoad] = useState(false)
  const [auto_save, setAutoSave] = useState(false)
  const [input_file, set_input_file] = useState<Blob | undefined>(undefined)
  const [input_format, set_input_format] = useState<FormatType>('excel')
  const [output_format, set_output_format] = useState<FormatType>('json')
  // Options pour chaque format de sortie
  const [output_options_excel, set_output_options_excel] = useState(getDefaultOutputOptions(output_config['excel']))
  const [output_options_json, set_output_options_json] = useState(getDefaultOutputOptions(output_config['json']))
  const [output_options_base, set_output_options_base] = useState(getDefaultOutputOptions(output_config['base']))
  // Options pour chaque format d'entrée (réservé pour plus tard)
  const [input_options_excel, set_input_options_excel] = useState(getDefaultInputOptions(input_config['excel']))
  const [input_options_json, set_input_options_json] = useState(getDefaultInputOptions(input_config['json']))
  const [input_options_base, set_input_options_base] = useState(getDefaultOutputOptions(input_config['base']))
  //const [input_options_blob, set_input_options_blob] = useState(getDefaultInputOptions('blob'))
  const [launch_at_opening, setLaunchAtOpening] = useState(false)
  const [show_terminal, set_show_terminal] = useState(true)
  const [config, setConfig] = useState<ConverterConfig>(CONVERTER_CONFIGS['universal'])
  const [failure_status, setFailureStatus] = useState(t('Menu.failure_file'))
  const [_, setSuccessStatus] = useState(t('Menu.loaded_file'))

  const [file_path, setFilePath] = useState('')
  const [failure, setFailure] = useState(false)

  // Restore a previously-picked file handle when the modal is reopened.
  // The handle survives in the module-level Map even after unmount.
  useEffect(() => {
    const stored = stored_file_handles.get(dialog_name)
    if (!stored) return
    stored.getFile()
      .then((file) => set_input_file(file))
      .catch(() => {
        // file moved/deleted or permission lost — drop it silently
        stored_file_handles.delete(dialog_name)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Switching input format must clear any previously picked file so the UI
  // does not display a stale filename from the other format (e.g. an Excel
  // file name lingering after switching to JSON).
  const change_input_format = (format: FormatType) => {
    set_input_format(format)
    set_input_file(undefined)
    stored_file_handles.delete(dialog_name)
  }

  const pick_input_file_with_handle = async () => {
    try {
      // @ts-expect-error showOpenFilePicker is not in all TS lib.dom versions
      const [handle] = await window.showOpenFilePicker({
        multiple: false,
        types: pickerTypesForFormat(input_format)
      }) as [FileSystemFileHandle]
      stored_file_handles.set(dialog_name, handle)
      const file = await handle.getFile()
      set_input_file(file)
    } catch (e) {
      if ((e as Error).name !== 'AbortError') {
        console.error('showOpenFilePicker failed:', e)
      }
    }
  }

  const [processing, setProcessing] = useState(false)
  const [started, setStarted] = useState(false)
  const [result, setResult] = useState('')

  const getCurrentOutputOptions = () => {
    switch (output_format) {
      case 'excel': return output_options_excel
      case 'json': return output_options_json
      case 'blob': return {}
      case 'example_excel': return {}
      case 'example_json': return {}
    }
  }

  const getCurrentInputOptions = () => {
    switch (input_format) {
      case 'excel': return input_options_excel
      case 'json': return input_options_json
      case 'blob': return {}
      case 'example_excel': return {}
      case 'example_json': return {}
    }
  }

  const initialize = (config: ConverterConfig, file_path: string, launch_at_opening: boolean) => {
    // The dialog is reused across "Open JSON" / "Open Excel" / etc., so a
    // previously picked file and the prior run's status (success/failure
    // banner, terminal output) must not leak into the next opening.
    set_input_file(undefined)
    stored_file_handles.delete(dialog_name)
    setStarted(false)
    setProcessing(false)
    setFailure(false)
    setResult('')
    const input_format = getInitialFormat(config.input.format, 'excel')
    set_input_format(input_format)
    const output_format = getInitialFormat(config.output.format, 'json')
    set_output_format(getInitialFormat(config.output.format, 'json'))
    setAutoLoad(!config.output.required)
    setAutoSave(
      input_format == 'blob' && output_format == 'excel' || //save excel
      !config.input.required && input_format != 'example_json' && input_format != 'example_excel' && input_format != 'blob'
    )
    setAutoLayout(input_format == 'blob')
    setConfig(config)

    // Reset options to defaults for the new config
    set_input_options_excel(getDefaultInputOptions(input_config['excel']))
    set_input_options_json(getDefaultInputOptions(input_config['json']))
    set_input_options_base(getDefaultOutputOptions(input_config['base']))
    set_output_options_excel(getDefaultOutputOptions(output_config['excel']))
    set_output_options_json(getDefaultOutputOptions(output_config['json']))
    set_output_options_base(getDefaultOutputOptions(output_config['base']))

    setLaunchAtOpening(launch_at_opening)
    set_show_terminal(false)
    if (config.failure_status) {
      setFailureStatus(config.failure_status)
    }
    if (config.success_status) {
      setSuccessStatus(config.success_status)
    }
    if (launch_at_opening) {
      launch(file_path)
    }
  }

  app_data.menu_configuration.ref_universal_converter_set_config.current = initialize

  const reset = () => {
    const form_data = new FormData()
    fetch(window.location.origin + url_prefix + '/upload/clean', {
      method: 'POST',
      body: form_data
    })
    setStarted(false)
    setProcessing(false)
    setFailure(false)
    setAutoLoad(!config.output.required /*&& !config.input.format.options!.includes('blob')*/)

    set_show_terminal(false)
    // if (input_format == 'example') {
    //   setLaunchAtOpening(true)
    // }
  }

  const launch = (file_path: string) => {
    setFilePath(file_path)
    setFailure(false)
    setStarted(true)
    setProcessing(true)
    setResult('')
    set_show_terminal(true)
  }

  const handleFinish = async () => {
    if (auto_save) {
      downloadFileResult()
      app_data.menu_configuration.dict_setter_show_dialog.ref_setter_show_modal_file_converter.current!(false)
      return
    }
    if (!auto_load) {
      console.log('🔄 Conversion terminée - en attente d\'action utilisateur')
      setStarted(false)
      setProcessing(false)
      setFailure(false)
      return
    }


    // ========== MODE CHARGEMENT CLASSIQUE ==========
    const path = window.location.origin
    const url = path + url_prefix + 'upload/retrieve_result'
    const form_data = new FormData()

    const fetchData = {
      method: 'POST',
      body: form_data
    }
    // app_data.sendWaitingToast(
    //   async () => {
    try {

      const response = await fetch(url, fetchData)
      if (response.ok) {
        const arrayBuffer = await response.arrayBuffer()
        //const uint8Array = new Uint8Array(arrayBuffer)
        const decompressed = await decompressGzipDataFixed(arrayBuffer)
        const jsonData = JSON.parse(decompressed)
        if (input_format == 'example_json') {
          app_data.fromJSON(jsonData as Type_JSON, {} /*output_options_json*/)
        } else {
          retrieveJSONResults(
            app_data,
            jsonData,
            auto_layout,
            output_options_json as Type_JSON,
            app_data.layout_h_spacing ?? undefined,
            app_data.layout_v_spacing ?? undefined,
            app_data.layout_optimize_crossing,
            app_data.layout_sources_mode,
            app_data.layout_sinks_mode
          )
        }
        //setAutoLoad(false)
      }
    } catch (error) {
      setResult('FAILED Erreur chargement JSON:' + error)
      setProcessing(false)
      //setStarted(false)
      setFailure(true)
      return
    }

    //setStarted(false)
    setProcessing(false)
    setFailure(false)
    // })
  }

  const downloadFileResult = () => {
    const path = window.location.origin
    const url = path + url_prefix + 'upload/retrieve_result'
    const form_data = new FormData()
    form_data.append('input_format', input_format)
    form_data.append('output_format', output_format)
    const output_options = getCurrentOutputOptions()
    form_data.append('output_options', JSON.stringify(output_options))
    const input_options = getCurrentInputOptions()
    form_data.append('input_options', JSON.stringify(input_options))
    const fetchData = {
      method: 'POST',
      body: form_data
    }

    fetch(url, fetchData)
      .then(response => {
        if (!response.ok) {
          throw new Error('File not found')
        }
        return response.blob()
      })
      .then(blob => {
        // Déterminer le nom selon le format de sortie
        const extensions: Record<string, string> = {
          excel: '.xlsx',
          json: '.json'
        }
        //@ts-expect-error xxx
        let root_filename = input_file ? input_file.name.split('.')[0] : 'output'
        if (config.title == "ProcessDialog.reconciliation") {
          //@ts-expect-error xxx
          root_filename = input_file.name.split('.')[0] + 'reconciled'
        }

        const filename = `${root_filename}${extensions[output_format] || ''}`

        FileSaver.saveAs(blob, filename)
        console.log(`✅ Fichier téléchargé: ${filename}`)
      })
      .catch(error => {
        console.error('❌ Erreur téléchargement:', error)
        alert('Impossible de télécharger le fichier converti')
      })
  }
  const loadFileResult = () => {
    setAutoLoad(true)
    launch('')
    const form_data = new FormData()
    form_data.append('input_format', input_format)
    form_data.append('output_format', output_format)
    const output_options = getCurrentOutputOptions()
    form_data.append('output_options', JSON.stringify(output_options))
    const input_options = getCurrentInputOptions()
    form_data.append('input_options', JSON.stringify(input_options))
    fetch(window.location.origin + url_prefix + '/upload/retrieve_json', {
      method: 'POST',
      body: form_data
    })
  }
  /**
   * Gère le clic sur le bouton Convertir
   */
  const generic_process = async () => {
    // If we have a persisted handle, re-read the file from disk now — this is
    // what makes a second "Ouvrir" work after the user has modified the file.
    let current_input_file: Blob | undefined = input_file
    const handle = stored_file_handles.get(dialog_name)
    if (handle) {
      try {
        current_input_file = await handle.getFile()
        set_input_file(current_input_file)
      } catch (e) {
        console.error('Failed to re-read file from handle:', e)
        alert('Impossible de relire le fichier (supprimé, déplacé ou permission perdue). Veuillez le re-sélectionner.')
        stored_file_handles.delete(dialog_name)
        set_input_file(undefined)
        return
      }
    }

    if (input_format != 'blob' && input_format != 'example_excel' && input_format != 'example_json' && !current_input_file) {
      alert(t('ProcessDialog.select_file') || 'Veuillez sélectionner un fichier')
      return
    }
    launch('')
    // Préparer la requête
    const form_data = new FormData()
    const output_options = getCurrentOutputOptions()
    form_data.append('output_options', JSON.stringify(output_options))
    const input_options = getCurrentInputOptions()
    form_data.append('input_options', JSON.stringify(input_options))
    if (input_format == 'blob') {
      form_data.append('data', JSON.stringify(app_data.toJSON({ ...output_options_base, ...output_options } as Type_JSON)))
    } else if (input_format == 'example_excel' || input_format == 'example_json') {
      form_data.append('file_name', file_path)
    } else {
      form_data.append('file', current_input_file as Blob)
    }
    form_data.append('input_format', input_format)
    form_data.append('output_format', output_format)

    if (input_format == 'blob' && output_format == 'json') {
      app_data.saveToJSON({ ...output_options_base, ...output_options } as Type_JSON)
      setStarted(false)
      setProcessing(false)
      setFailure(false)
      app_data.menu_configuration.dict_setter_show_dialog.ref_setter_show_modal_file_converter.current!(false)
      return
    } else if (input_format == 'json' && output_format == 'blob') {
      decompressUploadedFileUniversal(current_input_file as File).then(JSON_data => {
        app_data.fromJSON(JSON_data as Type_JSON, input_options as Type_JSON)
      })
      setStarted(false)
      setProcessing(false)
      setFailure(false)
      return
    }
    const url = window.location.origin + config.server_endpoint

    console.log(`🔄 Lancement conversion ${input_format} → ${output_format}`)
    console.log('Options entrée:', input_options)
    console.log('Options sortie:', output_options)

    set_show_terminal(true)
    fetch(url, { method: 'POST', body: form_data })
    // .then(response => {
    //   if (!response.ok) {
    //     console.error('❌ Erreur lancement conversion:')

    //     // Mettre à jour les états pour indiquer l'échec
    //     setStarted(false)
    //     setProcessing(false)
    //     setFailure(true)
    //     setResult('❌ Erreur lancement conversion:')
    //   }
    //   return response.json()
    // })
    // .then(data => {
    //   console.log('✅ Conversion lancée avec succès:', data)
    // })
    // .catch(error => {
    //   console.error('❌ Erreur lancement conversion:', error)

    //   // Mettre à jour les états pour indiquer l'échec
    //   setStarted(false)
    //   setProcessing(false)
    //   setFailure(true)
    //   setResult('❌ Erreur lancement conversion:')
    // })
  }

  if (launch_at_opening) {
    if (processing && started) {
      generic_process()
      setLaunchAtOpening(false)
    }
  }

  const content = (
    <Box layerStyle='menuconfigpanel_grid'>
      {/* Section Input */}
      <FileFormatSection
        section_type='input'
        app_data={app_data}
        lang={lang}
        format_config={config.input.format}
        required={config.input.required}
        current_format={input_format}
        set_current_format={change_input_format}
        set_file={set_input_file}
        pick_file_handle={supports_fs_access ? pick_input_file_with_handle : undefined}
        //@ts-expect-error Blob has no .name, but File does — and that's what we actually store here
        current_file_name={input_file?.name}
        options_excel={input_options_excel as Record<string, unknown>}
        set_options_excel={(opts) => set_input_options_excel(opts as typeof input_options_excel)}
        options_json={input_options_json as Record<string, unknown>}
        set_options_json={(opts) => set_input_options_json(opts as typeof input_options_json)}
        options_base={input_options_base as Record<string, unknown>}
        set_options_base={(opts) => set_input_options_base(opts as typeof input_options_base)}
        input_config={input_config}
        output_config={output_config}
      />
      {config.input.required && config.output.required ? <Divider borderBottomWidth='2px' opacity='1' borderColor='primaire.2' /> : <></>}
      {/* Section Output */}
      <FileFormatSection
        section_type='output'
        app_data={app_data}
        lang={lang}
        format_config={config.output.format}
        required={config.output.required}
        current_format={output_format}
        set_current_format={set_output_format}
        options_excel={output_options_excel as Record<string, unknown>}
        set_options_excel={(opts) => set_output_options_excel(opts as typeof output_options_excel)}
        options_json={output_options_json as Record<string, unknown>}
        set_options_json={(opts) => set_output_options_json(opts as typeof output_options_json)}
        options_base={output_options_base as Record<string, unknown>}
        set_options_base={(opts) => set_output_options_base(opts as typeof output_options_base)}
        input_config={input_config}
        output_config={output_config}
      />
      {(config.output.required && output_format == 'blob') || (input_format == 'excel' || input_format == 'example_excel') ? <><Divider borderBottomWidth='2px' opacity='1' borderColor='primaire.2' />
        <WrapperBoxSubSectionMenu
          new_data={app_data}
          is_open={false}
          with_border={false}
          title={t('ProcessDialog.layout_section')}
        >
          <Box display='flex' flexDirection='column' gap='4px'>
            {config.output.required && output_format == 'blob' && <Checkbox
              variant='menuconfigpanel_option_checkbox'
              isChecked={auto_layout}
              onChange={(e: { target: { checked: boolean } }) => setAutoLayout(e.target.checked)}
            >
              <OSTooltip label={t('ProcessDialog.layout_from_displayed_tt')}>
                {t('ProcessDialog.layout_from_displayed')}
              </OSTooltip>
            </Checkbox>}
            {!auto_layout && <Box mt='4px'>
              <AutoLayoutSpacingInputs app_data={app_data} show_extremities show_optimize_mode />
            </Box>}
          </Box>
        </WrapperBoxSubSectionMenu></>
        : <></>}


      {/* Bouton de lancement */}
      {!launch_at_opening ? <>
        <Divider borderBottomWidth='2px' opacity='1' borderColor='primaire.2' />
        <Box layerStyle='menuconfigpanel_row_3cols'>
          <Box />
          <Box />
          <Button
            variant="menuconfigpanel_option_button_secondary"
            size='sizeButtonDialog'
            isDisabled={input_format !== 'blob' && config.input.required && !input_file}
            onClick={generic_process}
          >
            {t(config.launch_button_label)}
          </Button>
        </Box></> : <></>}
      {config.input.required && input_format !== 'blob' && !input_file ? <Alert status='warning'>
        <AlertIcon />
        {t('ProcessDialog.waiting_file')}
      </Alert> : <></>}
      {started && !processing && !failure ? (
        <Alert status='success'>
          <AlertIcon />
          {t('ProcessDialog.success')}
        </Alert>
      ) : (<></>)}
      {started && !processing && failure ? (
        <Alert status='warning'>
          <AlertIcon />
          {failure_status}
        </Alert>
      ) : (<></>)}
      {/* Terminal inline si configuré */}
      {show_terminal && (
        <>
          {/* ========== BOUTONS D'ACTION ========== */}
          <ActionButtons
            t={t}
            reset={reset}
            downloadFileResult={downloadFileResult}
            loadFileResult={loadFileResult}
            auto_load={auto_load}
            processing={processing}
            failure={failure}
          />
          <Divider borderBottomWidth='2px' opacity='1' borderColor='primaire.2' />
          <Box
            as='span'
            layerStyle='box_content_config'>
            <center><span className='title_box2'>Terminal</span></center>
          </Box>
          <ProcessTerminal
            url_prefix={app_data.url_prefix}
            app_data={app_data}
            handleFinish={handleFinish}
            downloadFileResult={downloadFileResult}
            loadFileResult={loadFileResult}
            auto_load={auto_load}
            failure={failure}
            setFailure={setFailure}
            processing={processing}
            setProcessing={setProcessing}
            started={started}
            result={result}
            setResult={setResult}
          />
        </>
      )}
    </Box>
  )

  return (
    <MenuDraggable
      dict_hook_ref_setter_show_dialog_components={app_data.menu_configuration.dict_setter_show_dialog}
      //@ts-expect-error xxx
      dialog_name={dialog_name}
      content={content}
      title={t(config.title)}
      minW={'25vw'}
      maxW={'50vw'}
    />
  )
}

